import { useMemo, useState } from 'react';
import { shapeToVectorLayer, useLayers } from '@ais-ui/react-openlayers';
import { useLocalization } from '@ais-ui/locale-management';
import { useToast } from '@ais-ui/ui-core';
import { useRouteFetch } from '@ais-ui/api';
import { GeoJson } from '@ais-ui/api-spec/shapes';

interface RouteFetchAndDrawProps {
  mmsi: string;
  locode: string;
  shipName?: string;
  portName?: string;
}

const useRouteFetchAndDraw = ({
  mmsi,
  locode,
  shipName,
  portName,
}: RouteFetchAndDrawProps) => {
  const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);

  const { addLayer, zoomToLayer } = useLayers();
  const { messages } = useLocalization();
  const { toast } = useToast();

  const fetchRoute = useRouteFetch();
  const routeId = useMemo(() => {
    const numberMmsi = parseInt(mmsi);

    return `${numberMmsi}-${locode}`;
  }, [mmsi, locode]);

  const onFetchRoute = async () => {
    setIsCalculatingRoute(true);
    toast({
      title: messages.genericToastTitleStarted,
      description: 'Started fetching route...',
    });

    try {
      const numberMmsi = parseInt(mmsi);

      const result = await fetchRoute(numberMmsi, locode);

      // Only proceed if result is successful and contains the necessary data
      if (result && result.route && result.route.coordinates) {
        const shipNameWithFallback = shipName ?? mmsi;
        const portNameWithFallback = portName ?? locode;
        const name = `${shipNameWithFallback}'s route to ${portNameWithFallback}`;

        const routeGeoJson: GeoJson = {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: result.route.coordinates,
          },
          properties: {
            distance: result.distance,
            eta: result.eta,
            sog: result.sog,
            status: result.status,
            name: name,
            id: routeId,
          },
        };

        const layer = shapeToVectorLayer(routeGeoJson);

        addLayer(layer, name, true, true, true, undefined, routeId);
        zoomToLayer(layer);
        toast({
          title: messages.genericToastTitleSuccess,
          description: 'Successfully fetched route',
        });
      } else {
        console.warn(
          'Route data is missing or incomplete in the fetch result.'
        );
      }
    } catch (error) {
      console.error('Error fetching route:', error);
      toast({
        title: messages.genericToastTitleError,
        description: 'Failed to fetch route',
        variant: 'destructive',
      });
    } finally {
      setIsCalculatingRoute(false);
    }
  };

  return {
    onFetchRoute,
    isCalculatingRoute,
  };
};

export default useRouteFetchAndDraw;
